import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Home, Search, Smartphone, ChevronLeft, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const formatMoney = (value: number) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getOrganizationName = (row: any) =>
  row?.organization_name ||
  row?.organizationName ||
  row?.company_name ||
  row?.customer_name ||
  row?.name ||
  "";

const exportCsv = (rows: any[]) => {
  const header = ["Sl.No", "Organization", "Limit Fixed", "Balance Due"];
  const body = rows.map((row, index) => [
    index + 1,
    getOrganizationName(row),
    Number(row.credit_limit || 0).toFixed(3),
    Number(row.current_balance || 0).toFixed(3),
  ]);
  const csv = [header, ...body]
    .map((cols) => cols.map((col) => `"${String(col).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "credit-limit-report.csv";
  link.click();
  URL.revokeObjectURL(url);
};

export default function CreditLimitReport() {
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});

  const { data: customers = [], isLoading } = useQuery({
    queryKey: ["/api/credit-customers"],
    queryFn: async () => {
      const response = await fetch("/api/credit-customers", {
        credentials: "include",
      });
      const result = await response.json();
      if (!result.ok) throw new Error(result.error || "Failed to fetch credit customers");
      return result.rows || [];
    },
  });

  const filteredCustomers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return customers;

    return customers.filter((customer: any) => {
      const haystack = [
        getOrganizationName(customer),
        customer.email,
        customer.mobile_number,
        customer.phone_number,
        customer.current_balance,
        customer.credit_limit,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [customers, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredCustomers.length / 7));
  const safePage = Math.min(page, totalPages);
  const startIndex = filteredCustomers.length === 0 ? 0 : (safePage - 1) * 7;
  const visibleRows = filteredCustomers.slice(startIndex, startIndex + 7);
  const allVisibleSelected = visibleRows.length > 0 && visibleRows.every((row: any) => selectedIds[row.id]);

  const toggleAllVisible = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = { ...prev };
      visibleRows.forEach((row: any) => {
        next[row.id] = checked;
      });
      return next;
    });
  };

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-sm text-slate-500">
        Loading credit limit report...
      </div>
    );
  }

  return (
    <div className="space-y-5 bg-slate-50 min-h-full">
      <div className="h-11 -mx-6 -mt-6 px-6 flex items-center bg-gradient-to-r from-slate-100 to-white border-b border-slate-100">
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <Home className="h-4 w-4 text-blue-900" />
          <span className="font-bold text-slate-900">Dashboard</span>
          <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
          <span>Credit Limit Reports</span>
        </div>
      </div>

      <div className="px-3">
        <Button
          type="button"
          className="h-11 bg-orange-100 text-slate-950 hover:bg-orange-200 shadow-sm border border-orange-200 gap-2 rounded-md font-medium"
        >
          SEND SMS TO SELECTED
          <Smartphone className="h-6 w-6 text-slate-900" />
        </Button>
      </div>

      <div className="mx-0 rounded-lg border border-slate-200 bg-white shadow-lg">
        <div className="flex flex-col gap-4 px-3 sm:px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3 text-xs text-slate-900">
            <span>Show:</span>
            <select className="h-8 w-[72px] border border-slate-300 bg-white px-3 text-xs outline-none">
              <option>All</option>
            </select>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex items-center">
              <Button
                type="button"
                variant="outline"
                className="h-8 rounded-r-none border-green-500 text-green-600 hover:text-green-700 px-4 text-xs font-semibold"
                onClick={() => exportCsv(filteredCustomers)}
              >
                CSV
              </Button>
              <Button
                type="button"
                variant="outline"
                className="h-8 rounded-l-none border-red-500 text-red-600 hover:text-red-700 px-4 text-xs font-semibold"
                onClick={handlePrint}
              >
                PDF
              </Button>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-900">Filter:</span>
              <div className="relative">
                <Input
                  value={searchQuery}
                  onChange={(event) => {
                    setSearchQuery(event.target.value);
                    setPage(1);
                  }}
                  placeholder="Type to filter..."
                  className="h-8 w-[220px] rounded-lg border-slate-300 pr-9 text-xs"
                />
                <Search className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto px-3 sm:px-3">
          <table className="w-full min-w-[760px] table-fixed border-collapse text-xs">
            <colgroup>
              <col className="w-[6%]" />
              <col className="w-[9%]" />
              <col className="w-[38%]" />
              <col className="w-[23%]" />
              <col className="w-[24%]" />
            </colgroup>
            <thead>
              <tr className="border-y border-slate-300 text-slate-950">
                <th className="px-3 py-3 text-left font-bold">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={allVisibleSelected}
                      onCheckedChange={(value) => toggleAllVisible(Boolean(value))}
                      className="h-3.5 w-3.5 rounded-none border-slate-400"
                    />
                    <span>All</span>
                  </div>
                </th>
                <th className="px-3 py-3 text-left font-bold">Sl.No</th>
                <th className="px-3 py-3 text-left font-bold">Organization</th>
                <th className="px-3 py-3 text-right font-bold">Limit Fixed</th>
                <th className="px-3 py-3 text-right font-bold">Balance Due</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((customer: any, rowIndex: number) => (
                <tr key={customer.id || rowIndex} className="border-b border-slate-200 odd:bg-slate-50 even:bg-white">
                  <td className="px-3 py-2.5">
                    <Checkbox
                      checked={Boolean(selectedIds[customer.id])}
                      onCheckedChange={(value) =>
                        setSelectedIds((prev) => ({ ...prev, [customer.id]: Boolean(value) }))
                      }
                      className="h-3.5 w-3.5 rounded-none border-slate-400"
                    />
                  </td>
                  <td className="px-3 py-2.5 text-slate-900">{startIndex + rowIndex + 1}</td>
                  <td className="truncate px-3 py-2.5 text-slate-900">{getOrganizationName(customer) || "-"}</td>
                  <td className="px-3 py-2.5 text-right text-slate-900">{formatMoney(customer.credit_limit)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-900">{formatMoney(customer.current_balance)}</td>
                </tr>
              ))}
              {visibleRows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-10 text-center text-slate-500">
                    No credit customers found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-slate-900 px-4 py-3 text-xs text-slate-900 sm:flex-row sm:items-center sm:justify-between">
          <span>
            Showing {filteredCustomers.length === 0 ? 0 : startIndex + 1} to{" "}
            {Math.min(startIndex + visibleRows.length, filteredCustomers.length)} of {filteredCustomers.length} entries
          </span>
          <div className="flex items-center gap-4 self-end sm:self-auto">
            <button
              type="button"
              className="px-2 text-slate-500 disabled:opacity-30"
              disabled={safePage <= 1}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-8 border border-slate-400 bg-slate-100 px-3 py-2 text-center">{safePage}</span>
            <button
              type="button"
              className="px-2 text-slate-500 disabled:opacity-30"
              disabled={safePage >= totalPages}
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

